import React, { useState } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Pie } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

// Register necessary components
ChartJS.register(ArcElement, ChartTooltip, Legend);

const HdfcGoldLoanEmiCal = () => {
  const [loanAmount, setLoanAmount] = useState(5000000);
  const [interestRate, setInterestRate] = useState(7.5);
  const [loanTenure, setLoanTenure] = useState(20);
  const [monthlyEMI, setMonthlyEMI] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);
  const [activeYear, setActiveYear] = useState(null);

  const calculateEMI = () => {
    const principal = loanAmount;
    const annualInterest = interestRate / 100;
    const monthlyInterest = annualInterest / 12;
    const tenureInMonths = loanTenure * 12;

    const emi =
      (principal *
        monthlyInterest *
        Math.pow(1 + monthlyInterest, tenureInMonths)) /
      (Math.pow(1 + monthlyInterest, tenureInMonths) - 1);

    const totalAmount = emi * tenureInMonths;
    const totalInterest = totalAmount - principal;

    setMonthlyEMI(emi.toFixed(2));
    setTotalInterest(totalInterest.toFixed(2));
    setTotalAmount(totalAmount.toFixed(2));

    const schedule = [];
    let balance = principal;
    // let year = new Date().getFullYear();
    let currentYear = new Date().getFullYear();

    let currentMonth = 6;

    for (let i = 0; i < tenureInMonths; i++) {
      const interestForMonth = balance * monthlyInterest;
      const principalForMonth = emi - interestForMonth;
      balance -= principalForMonth;

      schedule.push({
        // month: i + 1,
        // year: year,
        month: ((currentMonth + i) % 12) + 1, // Ensure month is in range 1-12
        year: currentYear + Math.floor((currentMonth + i) / 12),
        principalPaid: principalForMonth,
        interestPaid: interestForMonth,
        totalPayment: emi,
        balance: balance,
      });

      // if ((i + 1) % 12 === 0) {
      //   year++;
      // }
    }

    setAmortizationSchedule(schedule);
  };

  const handleLoanAmountChange = (e) => {
    const value = e.target.value;
    setLoanAmount(value === "" ? 0 : parseInt(value));
  };

  const handleInterestRateChange = (e) => {
    const value = e.target.value;
    setInterestRate(value === "" ? 0 : parseFloat(value));
  };

  const handleLoanTenureChange = (e) => {
    const value = e.target.value;
    setLoanTenure(value === "" ? 0 : parseInt(value));
  };

  const handleYearToggle = (year) => {
    setActiveYear(activeYear === year ? null : year);
  };

  const data = {
    labels: ["Principal Amount", "Total Interest"],
    datasets: [
      {
        label: "EMI Breakdown",
        data: [loanAmount, totalInterest],
        backgroundColor: ["#36A2EB", "#FF6384"],
      },
    ],
  };
  const categories = [
    { id: 1, name: "Category 1", link: "/" },
    { id: 2, name: "Category 2", link: "/" },
    { id: 3, name: "Category 3", link: "/" },
    { id: 4, name: "Category 4", link: "/" },
    // Add more categories as needed
  ];
  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={`${
          id === open ? "rotate-180" : ""
        } h-5 w-5 transition-transform`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  }

  const [activeIndex, setActiveIndex] = useState(0);

  const faqs = [
    {
        "question": "Is this calculator specific to HDFC Bank gold loans?",
        "answer": "Yes. It’s tailored to HDFC Bank’s interest rate structure and loan terms, but you can adjust interest and tenure to simulate other lenders as well."
      },
      {
        "question": "How does foreclosure impact interest?",
        "answer": "Repaying the loan early stops interest accrual from the date of repayment. This can save significant interest, as EMIs in early months are interest-heavy."
      },
      {
        "question": "Can I make part-prepayments on this loan?",
        "answer": "Yes. HDFC Bank allows partial prepayments. You can then choose to reduce EMI or shorten the loan tenure based on your financial goals."
      },
      {
        "question": "Do interest rates vary easily?",
        "answer": "Yes, interest rates on gold loans are influenced by RBI policies. A small change in rate can meaningfully alter your EMI, so compare before pledging."
      },
      {
        "question": "Does the loan amount depend on gold purity?",
        "answer": "Yes, the amount depends on the actual purity (karat) of the gold and live market rate. Higher purity gold gets higher loan amount and EMI."
      },
      {
        "question": "Is there any fee for valuation?",
        "answer": "HDFC Bank often offers free valuation for loans above a certain threshold. In some cases, small scrutiny fees may apply depending on branch policy."
      },
      {
        "question": "Does fluctuating gold price change my EMI?",
        "answer": "No. EMI is calculated on the disbursed principal. Gold price fluctuations may impact new top-up eligibility but not current EMI."
      },
      {
        "question": "Is there a penalty for prepayment?",
        "answer": "Typically, HDFC Bank does not charge penalties on early repayment and encourages closure, helping you save on interest."
      },
      {
        "question": "Can the pledged gold be returned early?",
        "answer": "Yes. Once the outstanding loan (principal + interest) is fully repaid, you can collect your gold without delay."
      },
      {
        "question": "Are there any silent charges like GST?",
        "answer": "GST applies to interest component but is usually included in the EMI. Processing fees or insurance (if chosen) may attract GST separately."
      },
      {
        "question": "What documents are needed to apply?",
        "answer": "You only need KYC documents and the gold (photos and valuation). HDFC Bank completes the process quickly, typically within hours."
      },
      {
        "question": "Can NRI customers use this calculator?",
        "answer": "Yes, NRIs with KYC-compliant accounts can use the tool. Loan disbursals and documentation may vary slightly as per RBI guidelines."
      },
      {
        "question": "How frequently can I recalculate EMI?",
        "answer": "Recalculate EMI whenever you receive new rate notifications, plan for prepayment, or adjust loan tenure for better affordability."
      },
      {
        "question": "Can joint applicants use this loan?",
        "answer": "Yes, joint borrowing is permitted. The EMI stays the same, but eligibility is assessed based on combined income and obligations."
      },
      {
        "question": "Is the tool free and private?",
        "answer": "Absolutely. It’s completely free, needs no sign-up, and your data remains confidential—ideal for realistic financial planning."
      }
    ]
  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };
  return (
    <>
      <Helmet>
           <title>HDFC Gold Loan EMI Calculator - Easy & Accurate Estimation</title>
          <meta  name='description' content='Use our HDFC gold loan EMI Calculator to calculate your monthly installments easily. Make informed decisions with precise EMI estimates.'/>
          <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/hdfc-gold-loan-emi-calculator" />
          <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
          <meta name="robots" content="max-video-preview:-1" />
        </Helmet>
        <FAQSchema faqs={faqs} />
      {/* <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <Link
              to="/financial-tools-calculators"
              className="text-blue-600 hover:text-blue-800"
            >
              Financial Tools Calculators
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 ">
            HDFC Gold Loan EMI Calculator
            </span>
          </li>
        </ol>
      </nav> */}
          <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
        <AdBanner displayOrder="1" />
        </div>
      </div>
      <div className="py-4 ">
        <div className="container mx-auto p-4 shadow-xl rounded-md ">
          <h1 className="text-2xl font-bold mb-4">
          HDFC Gold Loan EMI Calculator
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Loan Amount
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    value={loanAmount}
                    onChange={handleLoanAmountChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">
                    ₹ {loanAmount.toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000000"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Rate of Interest (P.A.)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={interestRate}
                    onChange={handleInterestRateChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{interestRate} %</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="15"
                  step="0.1"
                  value={interestRate}
                  onChange={(e) => setInterestRate(parseFloat(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Loan Tenure (Years)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={loanTenure}
                    onChange={handleLoanTenureChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{loanTenure} Yr</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="30"
                  value={loanTenure}
                  onChange={(e) => setLoanTenure(parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <button
                onClick={calculateEMI}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Calculate
              </button>
            </div>
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold">Results</h2>
                <p>Monthly EMI: ₹ {parseFloat(monthlyEMI).toLocaleString()}</p>
                <p>Total Principal Amount: ₹ {loanAmount.toLocaleString()}</p>
                <p>
                  Total Interest: ₹ {parseFloat(totalInterest).toLocaleString()}
                </p>
                <p>
                  Total Amount (Principal + Interest): ₹{" "}
                  {parseFloat(totalAmount).toLocaleString()}
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold">Breakdown</h2>
                <div className="flex justify-center">
                  <Pie
                    data={data}
                    options={{ maintainAspectRatio: false }}
                    width={300}
                    height={300}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <h2 className="text-xl font-bold mb-4">Amortization Schedule</h2>
            {amortizationSchedule.length > 0 && (
              <div className="flex flex-col space-y-4">
                {Array.from(
                  new Set(amortizationSchedule.map((item) => item.year))
                ).map((year, idx) => (
                  <div key={idx} className="mb-4 border rounded-md shadow-md">
                    <div
                      className={`cursor-pointer text-lg font-semibold bg-gray-100 p-4 flex justify-between items-center ${
                        activeYear === year ? "bg-blue-100" : ""
                      }`}
                      onClick={() => handleYearToggle(year)}
                    >
                      <span>{year}</span>
                      <span>{activeYear === year ? "-" : "+"}</span>
                    </div>
                    {activeYear === year && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-md shadow-md text-center">
                          <thead>
                            <tr>
                              <th className="py-2 px-4 border-b">Month</th>
                              <th className="py-2 px-4 border-b">
                                Principal Paid
                              </th>
                              <th className="py-2 px-4 border-b">
                                Interest Charged
                              </th>
                              <th className="py-2 px-4 border-b">
                                Total Payment
                              </th>
                              <th className="py-2 px-4 border-b">Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {amortizationSchedule
                              .filter((item) => item.year === year)
                              .map((item, idx) => (
                                <tr key={idx}>
                                  <td className="py-2 px-4 border-b">
                                    {new Date(0, item.month - 1).toLocaleString(
                                      "default",
                                      { month: "short" }
                                    )}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.principalPaid
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.interestPaid
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.totalPayment
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹ {item.balance.toFixed(2).toLocaleString()}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
        <div className="w-full md:w-2/3 p-4 mx-auto">
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
        Plan Your HDFC Bank Gold Loan Repayments with TheFinMaster’s EMI Calculator
      </h2>
      <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
        A gold loan offers a fast, reliable way to access funds during emergencies or planned expenditures—such as weddings, medical bills, business capital, or travel. HDFC Bank provides gold loans with competitive interest rates, simple documentation, and quick processing. But before pledging your treasured jewellery, it’s essential to clearly understand your monthly repayment obligations. TheFinMaster’s HDFC Bank Gold Loan EMI Calculator helps you simulate your EMIs, analyze different repayment scenarios, and plan your financial outgo effectively—all before disbursal.
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
        What is a Gold Loan EMI?
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        A Gold Loan EMI (Equated Monthly Installment) is the fixed monthly payment you make to HDFC Bank to repay your gold-backed loan. Each EMI consists of two components:
        Principal: The actual loan amount disbursed against your gold
        Interest: The cost of borrowing charged on the outstanding balance
        EMIs are interest-heavy in the initial months, with increasing principal repayment as the loan progresses. Understanding this EMI structure helps you organize your finances better and make informed decisions about part-prepayment or early closure.
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
        What is the HDFC Bank Gold Loan EMI Calculator?
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        The HDFC Bank Gold Loan EMI Calculator by TheFinMaster is an intuitive, easy-to-use digital tool that calculates your monthly EMI based on three key inputs:
        <ul className="list-disc pl-6 mt-2">
          <li>Loan Amount – Principal value backed by gold</li>
          <li>Annual Interest Rate – Competitive rate set by HDFC Bank</li>
          <li>Loan Tenure – Repayment period in months</li>
        </ul>
        After entering these values, the calculator instantly provides:
        <ul className="list-disc pl-6 mt-2">
          <li>Your exact monthly EMI</li>
          <li>Total interest payable over the loan tenure</li>
          <li>Aggregate repayment amount (principal + interest)</li>
          <li>A detailed amortisation schedule that shows how each EMI reduces your outstanding balance</li>
        </ul>
        You can experiment with different inputs to compare repayment setups and select the one that best fits your financial situation.
      </p>
    </div>
    <div className="w-full flex justify-center items-center my-6 px-4">
          <div className="w-full max-w-[984px]">

     
        <AdBanner displayOrder="3" />
        </div>
         
        </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
        Why Use TheFinMaster’s HDFC Gold Loan EMI Calculator?
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        This calculator offers several advantages:
        <ul className="list-disc pl-6 mt-2">
          <li><strong>Fast & Point‑Accurate</strong>—calculates EMI instantly using proven formulas</li>
          <li><strong>Privacy Assured</strong>—no login or personal data required to use</li>
          <li><strong>Scenario Planning</strong>—adjust variables real-time to compare outcomes</li>
          <li><strong>Enhanced Transparency</strong>—see precisely how your EMI splits between interest and principal</li>
          <li><strong>Graphical Amortisation View</strong>—monitor how your loan balance declines every month</li>
          <li><strong>Mobile‑Ready Assistance</strong>—optimised for all devices for anytime accessibility</li>
        </ul>
        With these benefits, your journey to borrow wisely and repay confidently becomes simple and stress-free.
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
        How Does the HDFC Gold Loan EMI Calculator Work?
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        The EMI is calculated using this universally accepted formula:
      </p>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        EMI = [P × R × (1 + R)^N] / [(1 + R)^N – 1]
      </p>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        Where:
      </p>
      <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        <li>P = Principal loan amount</li>
        <li>R = Monthly interest rate (Annual Rate ÷ 12 ÷ 100)</li>
        <li>N = Loan tenure in months</li>
      </ul>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        <strong>Example Scenario:</strong><br />
        Assume you take a gold loan of ₹3,00,000 from HDFC Bank at an annual interest rate of 8.5%, repayable over 12 months.<br />
        P = ₹3,00,000<br />
        R = 0.7083% (8.5 ÷ 12 ÷ 100)<br />
        N = 12<br />
        Results:<br />
        EMI ≈ ₹26,003<br />
        Total Repayment ≈ ₹3,12,039<br />
        Total Interest ≈ ₹12,039<br />
        This practical example helps you visualize the exact EMI and total cost before making any financial commitments.
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
        Sample Amortisation Schedule (First 6 Months)
      </h2>
      <div className="mt-6">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-lg text-gray-600 mt-4 border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-4 py-2 text-left">Month</th>
                <th className="border px-4 py-2 text-left">EMI (₹)</th>
                <th className="border px-4 py-2 text-left">Interest (₹)</th>
                <th className="border px-4 py-2 text-left">Principal (₹)</th>
                <th className="border px-4 py-2 text-left">Outstanding Balance (₹)</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["1", "26,003", "2,125", "23,878", "2,76,122"],
                ["2", "26,003", "1,955", "24,048", "2,52,074"],
                ["3", "26,003", "1,784", "24,219", "2,27,855"],
                ["4", "26,003", "1,613", "24,390", "2,03,465"],
                ["5", "26,003", "1,441", "24,562", "1,78,903"],
                ["6", "26,003", "1,269", "24,734", "1,54,169"]
              ].map(([month, emi, interest, principal, balance], index) => (
                <tr key={index}>
                  <td className="border px-4 py-2">{month}</td>
                  <td className="border px-4 py-2">{emi}</td>
                  <td className="border px-4 py-2">{interest}</td>
                  <td className="border px-4 py-2">{principal}</td>
                  <td className="border px-4 py-2">{balance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:hidden space-y-4 mt-4">
          {[
            ["1", "26,003", "2,125", "23,878", "2,76,122"],
            ["2", "26,003", "1,955", "24,048", "2,52,074"],
            ["3", "26,003", "1,784", "24,219", "2,27,855"],
            ["4", "26,003", "1,613", "24,390", "2,03,465"],
            ["5", "26,003", "1,441", "24,562", "1,78,903"],
            ["6", "26,003", "1,269", "24,734", "1,54,169"]
          ].map(([month, emi, interest, principal, balance], index) => (
            <div key={index} className="bg-white shadow rounded-xl p-4 text-sm text-gray-700 space-y-1 border">
              <div><span className="font-semibold">Month:</span> {month}</div>
              <div><span className="font-semibold">EMI (₹):</span> {emi}</div>
              <div><span className="font-semibold">Interest (₹):</span> {interest}</div>
              <div><span className="font-semibold">Principal (₹):</span> {principal}</div>
              <div><span className="font-semibold">Outstanding Balance (₹):</span> {balance}</div>
            </div>
          ))}
        </div>
      </div>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        You can extend this schedule to the full loan tenure, download it for deeper insights, or use it for budgeting.
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
        Factors Affecting Your Gold Loan EMI
      </h2>
      <ol className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        <li><strong>Loan Amount:</strong> Higher loan amounts result in higher EMIs, so borrow housing needs, not desires.</li>
        <li><strong>Interest Rate:</strong> Even small shifts in the interest rate can magnify your total interest outgo.</li>
        <li><strong>Tenure:</strong> Shorter tenure increases EMI but reduces total interest, while a longer tenure decreases EMI but increases interest cost.</li>
        <li><strong>Gold Purity & Market Valuation:</strong> The loan is disbursed based on purity and live gold rate—changing gold value can impact future top-ups.</li>
        <li><strong>Loan-to-Value (LTV) Ratio:</strong> HDFC Bank generally offers up to 75% LTV; a lower LTV means less EMI for the same tenure.</li>
        <li><strong>Prepayment Options:</strong> HDFC often levies low or no penalty on early repayment, making them a cost-effective choice.</li>
        <li><strong>Outstanding Gold Loan:</strong> If you have an existing gold loan that isn’t closed, it may affect subsequent loan eligibility and EMI terms.</li>
      </ol>
    </div>
  </div>
          <div className="w-full md:w-1/3 p-4">
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
                <h2 className="text-lg font-semibold ">Popular Categories</h2>
              </div>
              <div className="p-2 ">
                <ul>
                  <Link to="/financial-tools-calculators/swp-calculator">
                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                      SWP Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/mutul-fund-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Mutual Fund Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Samriddhi Yojana Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/fd-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      FD Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/rd-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      RD Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/nps-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      NPS Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/hra-exemption-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      HRA Exemption Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/retirement-planning-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Retirement Planning Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      EMI Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/simple-interest-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Simple Interest Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/compound-interest-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Compound Interest Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/nsc-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      NSC Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/step-up-sip-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Step Up Sip Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/income-tax-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Income Tax Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Home Loan Emi Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Car Loan Emi Calculator
                    </li>
                  </Link>
                  {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                </ul>
              </div>
            </div>

                    {/* Ad Banner */}
              <div className="mt-4">
                <div className="bg-white shadow-md rounded-lg overflow-hidden">
                  <div className="p-3 w-full h-auto rounded-md object-cover">
                    <AdBanner displayOrder="2" />
                  </div>
                </div>
              </div>
            <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
              <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                <h2 className="text-lg font-semibold">More Categories</h2>
              </div>
              <div className="p-2">
                <ul>
                  <Link to="/financial-tools-calculators/gratuity-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Gratuity Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/APY-calculator">
                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                      APY Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/cagr-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      CAGR Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/gst-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      GST Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Flat Vs Reducing Rate Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/equity-margin-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Equity Margin Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/inflation-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Inflation Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/salary-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Salary Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/brokerage-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Brokerage Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/tds-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      TDS Calculator
                    </li>
                  </Link>
                  {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
        <h2 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default HdfcGoldLoanEmiCal;
